import dayjs from 'dayjs';
import {
  chartToBase64,
  generateTopChartConfig,
  generateDailyChartConfig,
  generateStackedChartConfig,
  generateFiliaisChartConfig,
  generateCarrosPorFilialChartConfig,
  generateHeatmapChartConfig
} from './chartToImage.js';

/**
 * Gera HTML para impressão do relatório operacional
 * @param {Object} params - Parâmetros do relatório
 * @returns {string} HTML completo para impressão
 */
export const generatePrintableHTML = async ({
  title = 'Relatório Operacional',
  organizationName = 'EtiquetandoAPP',
  data = [],
  periodo = '',
  filtros = {},
  metricas = {},
  topVeiculos = [],
  topClientes = [],
  topImpressoras = [],
  dailyAgg = [],
  theme = 'light',
  includeCharts = true,
  empresa = '',
  navbarInfo = {},
  externalChartImages = {}
}) => {

  // Renomear data para dados para manter compatibilidade
  const dados = data;

  // Lógica para HeaderPadrao (replicada de HeaderPadrao.jsx)
  const branchCode = navbarInfo?.empresa || empresa || '—'
  const branchAcronym = navbarInfo?.sigla || navbarInfo?.assignedBranch?.sigla || '—'
  const employeeName = navbarInfo?.nome || navbarInfo?.name || ''
  const dateLabel = periodo || filtros.periodo || ''

  // Funções auxiliares movidas para o topo para evitar ReferenceError
  // Função para agregar dados por critério
  const aggregateBy = (data, keyFn) => {
    const map = new Map();

    if (!Array.isArray(data)) return [];

    data.forEach(item => {
      const key = keyFn(item);
      if (!map.has(key)) {
        map.set(key, {
          label: key,
          etiquetasImp: 0,
          etiquetasSem: 0,
          notas: 0,
          volumes: 0,
          peso: 0
        });
      }

      const entry = map.get(key);
      entry.etiquetasImp += Number(item.TTL_ETIQUETAS_IMP || item.etiquetasImp || 0);
      entry.etiquetasSem += Number(item.TTL_ETIQUETAS_SEM || item.etiquetasSem || 0);
      entry.notas += 1;
      entry.volumes += Number(item.VOLUMES || item.volumes || 0);
      entry.peso += Number(item.PESO || item.peso || 0);
    });

    return Array.from(map.values());
  };

  // Função para calcular top filiais
  const getTopFiliais = () => {
    const filialAgg = aggregateBy(dados, (item) => {
      return item.FILIAL || item.filial || item.EMPRESA || item.empresa || 'N/A';
    });

    return filialAgg
      .map((item) => ({
        filial: item.label,
        total: item.etiquetasImp + item.etiquetasSem,
        impressas: item.etiquetasImp,
        pendentes: item.etiquetasSem
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  };

  // Função para calcular top carros por filial
  const getTopCarrosPorFilial = () => {
    const carroAgg = aggregateBy(dados, (item) => {
      const filial = item.FILIAL || item.filial || item.EMPRESA || item.empresa || 'N/A';
      const carro = item.CARRO || item.veiculo || item.carro || 'N/A';
      return `${filial}|${carro}`;
    });

    const filialCarroMap = new Map();

    carroAgg.forEach((item) => {
      const [filial, carro] = item.label.split('|');
      const total = item.etiquetasImp + item.etiquetasSem;

      if (!filialCarroMap.has(filial) || filialCarroMap.get(filial).total < total) {
        filialCarroMap.set(filial, {
          filial,
          carro,
          total,
          impressas: item.etiquetasImp,
          pendentes: item.etiquetasSem
        });
      }
    });

    return Array.from(filialCarroMap.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  };

  // Função para dados completos do heatmap
  const getHeatmapFullData = (dataArr) => {
    const daySet = new Set();
    const map = new Map();
    const order = { 'Seg': 1, 'Ter': 2, 'Qua': 3, 'Qui': 4, 'Sex': 5, 'Sáb': 6, 'Dom': 7 };

    dataArr.forEach(item => {
      const ts = item.PRIMEIRA_ETIQUETA ?? item.ULTIMA_ETIQUETA ?? item.DATA ?? item.DATA_EMISSAO ?? item.data ?? item.dataEmissao;
      if (!ts) return;

      const d = dayjs(ts);
      const weekdayLabel = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][d.day()];
      const hour = d.hour();
      daySet.add(weekdayLabel);

      const key = `${weekdayLabel}|${hour}`;
      const vol = Number(item.TTL_ETIQUETAS_IMP ?? item.TTL_ETIQUETAS ?? 0) + Number(item.TTL_ETIQUETAS_SEM ?? 0);
      map.set(key, (map.get(key) || 0) + vol);
    });

    const labelsX = Array.from(daySet).sort((a, b) => (order[a] ?? 99) - (order[b] ?? 99));
    const labelsY = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}h`);

    let max = 0;
    labelsX.forEach(day => {
      for (let h = 0; h < 24; h++) {
        const v = map.get(`${day}|${h}`) || 0;
        if (v > max) max = v;
      }
    });

    return { labelsX, labelsY, map, max };
  };

  // Função para calcular estatísticas do heatmap
  const getHeatmapStats = () => {
    if (!dados || dados.length === 0) return { hour: 'N/A', total: 0, day: 'N/A' };

    const hourTotals = new Map();
    let maxVolume = 0;
    let peakHour = 'N/A';
    let peakDay = 'N/A';

    dados.forEach(item => {
      const timestamp = item.DT_EMISSAO || item.dt_emissao || item.DATA || item.data;
      if (!timestamp) return;

      const date = dayjs(timestamp);
      const dayOfWeek = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][date.day()];
      const hour = date.hour();
      const volume = Number(item.TTL_ETIQUETAS_IMP || 0) + Number(item.TTL_ETIQUETAS_SEM || 0);

      const currentTotal = hourTotals.get(hour) || 0;
      hourTotals.set(hour, currentTotal + volume);

      if (volume > maxVolume) {
        maxVolume = volume;
        peakHour = `${String(hour).padStart(2, '0')}h`;
        peakDay = dayOfWeek;
      }
    });

    let maxHourTotal = 0;
    let bestHour = 'N/A';

    for (const [hour, total] of hourTotals.entries()) {
      if (total > maxHourTotal) {
        maxHourTotal = total;
        bestHour = `${String(hour).padStart(2, '0')}h`;
      }
    }

    return {
      hour: bestHour,
      total: maxHourTotal,
      peakSpecific: `${peakDay} ${peakHour}`,
      peakValue: maxVolume
    };
  };

  // Dados processados
  const topFiliais = getTopFiliais();
  const topCarros = getTopCarrosPorFilial();
  const heatmapStats = getHeatmapStats();

  // Gerar gráficos como imagens base64 se solicitado
  let chartImages = { ...(externalChartImages || {}) };
  if (includeCharts) {
    try {
      // Gráfico Top Veículos
      if (topVeiculos && topVeiculos.length > 0) {
        const topVeiculosConfig = generateTopChartConfig(topVeiculos.slice(0, 10), 'Top 10 Veículos por Etiquetas', theme, '#1890ff');
        chartImages.topVeiculos = await chartToBase64(topVeiculosConfig, 800, 400);
      }

      // Gráfico Top Clientes
      if (topClientes && topClientes.length > 0) {
        const topClientesConfig = generateTopChartConfig(topClientes.slice(0, 10), 'Top 10 Clientes por Etiquetas', theme, '#52c41a');
        chartImages.topClientes = await chartToBase64(topClientesConfig, 800, 400);
      }

      // Gráfico Top Impressoras
      if (topImpressoras && topImpressoras.length > 0) {
        const topImpressorasConfig = generateTopChartConfig(topImpressoras.slice(0, 10), 'Top 10 Impressoras por Etiquetas', theme, '#722ed1');
        chartImages.topImpressoras = await chartToBase64(topImpressorasConfig, 800, 400);
      }

      // Gráfico Volume Diário
      if (dailyAgg && dailyAgg.length > 0) {
        const dailyConfig = generateDailyChartConfig(dailyAgg, theme);
        chartImages.daily = await chartToBase64(dailyConfig, 800, 400);

        // Gráfico Empilhado
        const stackedConfig = generateStackedChartConfig(dailyAgg, theme);
        chartImages.stacked = await chartToBase64(stackedConfig, 800, 400);
      }

      // NOVOS GRÁFICOS SOLICITADOS

      // Gráfico Etiquetas por Filial
      if (topFiliais && topFiliais.length > 0) {
        const filiaisConfig = generateFiliaisChartConfig(topFiliais, theme);
        chartImages.filiais = await chartToBase64(filiaisConfig, 800, 400);
      }

      // Gráfico Top 1 Carro por Filial
      if (topCarros && topCarros.length > 0) {
        const carrosFilialConfig = generateCarrosPorFilialChartConfig(topCarros, theme);
        chartImages.carrosFilial = await chartToBase64(carrosFilialConfig, 800, 500); // Um pouco mais alto para caber os labels
      }

      // Gráfico Heatmap
      const heatmapData = getHeatmapFullData(dados);
      if (heatmapData && heatmapData.max > 0) {
        const heatmapConfig = generateHeatmapChartConfig(heatmapData, theme);
        chartImages.heatmap = await chartToBase64(heatmapConfig, 800, 600); // Mais alto para o heatmap
      }

    } catch (error) {
      console.warn('Erro ao gerar gráficos:', error);
      // Continuar sem gráficos em caso de erro
    }
  }

  // Gera o HTML completo
  return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Relatório Operacional - Painel de Etiquetas</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
  <style>
    @page {
      size: A4 portrait;
      margin: 1.27cm;
    }
    
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      line-height: 1.4;
      color: #1a1a1a;
      background: #ffffff;
      font-size: 10pt;
    }
    
    .report-container {
      width: 100%;
      max-width: 210mm;
      margin: 0 auto;
      background: #ffffff;
    }
    
    .header {
      background: #111827;
      color: white;
      padding: 12px 16px;
      border-bottom: 2px solid #4a5568;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
    }
    
    .header .branch-info {
      flex: 1;
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .header .branch-code {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #f59e0b;
      color: #111827;
      border-radius: 4px;
      padding: 4px 8px;
      font-weight: 700;
      font-size: 12px;
    }

    .header .branch-acronym {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #ef4444;
      color: #ffffff;
      border-radius: 4px;
      padding: 4px 8px;
      font-weight: 600;
      font-size: 12px;
    }

    .header .title-container {
      flex: 1;
      text-align: center;
    }

    .header h1 {
      font-size: 16px;
      font-weight: 600;
      margin: 0;
      color: #ffffff;
    }
    
    .header .user-info {
      flex: 1;
      text-align: right;
    }

    .header .user-name {
      font-size: 12px;
      opacity: 0.9;
    }

    .header .date-label {
      margin: 0;
      font-size: 12px;
    }
    
    .content {
      padding: 0.5rem;
    }
    
    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.8rem;
      margin-bottom: 1rem;
    }
    
    .info-card {
      background: #f8f9fa;
      border-radius: 4px;
      padding: 0.8rem;
      border-left: 3px solid #6c757d;
    }
    
    .info-card h3 {
      font-size: 0.8rem;
      font-weight: 600;
      color: #2d3748;
      margin-bottom: 0.6rem;
    }
    
    .info-item {
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.3rem;
      font-size: 0.75rem;
    }
    
    .section {
      margin-top: 1rem;
      page-break-inside: avoid;
    }
    
    .section-title {
      font-size: 0.9rem;
      font-weight: 600;
      color: #2d3748;
      margin-bottom: 0.5rem;
      padding-bottom: 0.3rem;
      border-bottom: 2px solid #e2e8f0;
    }
    
    .data-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.75rem;
      margin-top: 0.5rem;
    }
    
    .data-table th {
      background: #f8f9fa;
      color: #2d3748;
      font-weight: 600;
      padding: 0.4rem 0.5rem;
      border: 1px solid #e2e8f0;
      text-align: left;
    }
    
    .data-table td {
      padding: 0.3rem 0.5rem;
      border: 1px solid #e2e8f0;
      vertical-align: middle;
    }
    
    .data-table tbody tr:nth-child(even) {
      background: #f8f9fa;
    }
    
    @media print {
      body { background: white; }
      .section { page-break-inside: avoid; }
      .data-table { font-size: 0.7rem; }
      .info-grid { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="report-container">
    <div class="header">
      <div class="branch-info">
        <span class="branch-code">${branchCode}</span>
        <span class="branch-acronym">${branchAcronym}</span>
      </div>
      <div class="title-container">
        <h1>${title}</h1>
      </div>
      <div class="user-info">
        <div class="user-name">${employeeName}</div>
        <p class="date-label">${dateLabel}</p>
      </div>
    </div>
    
    <!-- Seção de Filtros -->
    <div class="content">
      <div class="section">
        <h2 class="section-title">🔍 Filtros para Geração de Relatórios</h2>
        <div class="info-grid">
          <div class="info-card" style="border-left-color: #722ed1;">
            <h3>Perãodo Selecionado</h3>
            <div class="info-item">
              <span>Data Inicial:</span>
              <strong>${filtros.dataInicial || 'N/A'}</strong>
            </div>
            <div class="info-item">
              <span>Data Final:</span>
              <strong>${filtros.dataFinal || 'N/A'}</strong>
            </div>
          </div>
          
          <div class="info-card" style="border-left-color: #13c2c2;">
            <h3>Empresas</h3>
            <div class="info-item">
              <span>Empresa Inicial:</span>
              <strong>${filtros.empresaInicial || 'N/A'}</strong>
            </div>
            <div class="info-item">
              <span>Empresa Final:</span>
              <strong>${filtros.empresaFinal || 'N/A'}</strong>
            </div>
          </div>
        </div>
      </div>
    
    <div class="content">
      <!-- Métricas Principais -->
      <div class="section">
        <h2 class="section-title">📊 Métricas Principais</h2>
        <div class="info-grid">
          <div class="info-card" style="border-left-color: #1890ff;">
            <h3>🚗 Veículos e Clientes</h3>
            <div class="info-item">
              <span>Total Veículos:</span>
              <strong>${(metricas.totalVeiculos || 0).toLocaleString()}</strong>
            </div>
            <div class="info-item">
              <span>Total Clientes:</span>
              <strong>${(metricas.totalClientes || 0).toLocaleString()}</strong>
            </div>
          </div>
          
          <div class="info-card" style="border-left-color: #52c41a;">
            <h3>📋 Notas e Etiquetas</h3>
            <div class="info-item">
              <span>Total Notas:</span>
              <strong>${(metricas.totalNotas || 0).toLocaleString()}</strong>
            </div>
            <div class="info-item">
              <span>Total Etiquetas:</span>
              <strong>${(metricas.totalEtiquetas || 0).toLocaleString()}</strong>
            </div>
            <div class="info-item">
              <span>Notas Impressas:</span>
              <strong style="color: #52c41a;">${(metricas.notasImpressas || 0).toLocaleString()}</strong>
            </div>
            <div class="info-item">
              <span>Notas Pendentes:</span>
              <strong style="color: #ff4d4f;">${(metricas.notasPendentes || 0).toLocaleString()}</strong>
            </div>
          </div>
          
          <div class="info-card" style="border-left-color: #fa8c16;">
            <h3>📦 Peso e Volumes</h3>
            <div class="info-item">
              <span>Total Peso (kg):</span>
              <strong>${(metricas.totalPeso || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
            </div>
            <div class="info-item">
              <span>Total Volumes:</span>
              <strong>${(metricas.totalVolumes || 0).toLocaleString()}</strong>
            </div>
          </div>
          
          <div class="info-card" style="border-left-color: #722ed1;">
            <h3>🖨️ Impressoras e Equipamentos</h3>
            <div class="info-item">
              <span>Total Impressoras:</span>
              <strong>${(metricas.totalImpressoras || 0).toLocaleString()}</strong>
            </div>
            <div class="info-item">
              <span>Total Marcas:</span>
              <strong>${(metricas.totalMarcas || 0).toLocaleString()}</strong>
            </div>
            <div class="info-item">
              <span>Total Modelos:</span>
              <strong>${(metricas.totalModelos || 0).toLocaleString()}</strong>
            </div>
            <div class="info-item">
              <span>Total Emuladores:</span>
              <strong>${(metricas.totalEmuladores || 0).toLocaleString()}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Veículos -->
      <div class="section">
        <h2 class="section-title">🚗 Top Veículos por Etiquetas</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 40px;">#</th>
              <th>Veículo</th>
              <th style="text-align: right;">Etiquetas</th>
              <th style="text-align: right;">%</th>
            </tr>
          </thead>
          <tbody>
            ${(topVeiculos || []).slice(0, 10).map((veiculo, index) => {
    const percentual = ((veiculo.etiquetasImp / (metricas.totalEtiquetas || 1)) * 100).toFixed(1);
    return `
                <tr>
                  <td style="text-align: center; font-weight: 600;">${index + 1}</td>
                  <td style="font-weight: 500;">${veiculo.label || 'N/A'}</td>
                  <td style="text-align: right; color: #1890ff;">${(veiculo.etiquetasImp || 0).toLocaleString()}</td>
                  <td style="text-align: right; font-weight: 500;">${percentual}%</td>
                </tr>
              `;
  }).join('')}
          </tbody>
        </table>
      </div>

      <!-- Top Clientes -->
      <div class="section">
        <h2 class="section-title">👥 Top Clientes por Etiquetas</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 40px;">#</th>
              <th>Cliente</th>
              <th style="text-align: right;">Etiquetas</th>
              <th style="text-align: right;">%</th>
            </tr>
          </thead>
          <tbody>
            ${(topClientes || []).slice(0, 10).map((cliente, index) => {
    const percentual = ((cliente.etiquetasImp / (metricas.totalEtiquetas || 1)) * 100).toFixed(1);
    return `
                <tr>
                  <td style="text-align: center; font-weight: 600;">${index + 1}</td>
                  <td style="font-weight: 500;">${cliente.label || 'N/A'}</td>
                  <td style="text-align: right; color: #52c41a;">${(cliente.etiquetasImp || 0).toLocaleString()}</td>
                  <td style="text-align: right; font-weight: 500;">${percentual}%</td>
                </tr>
              `;
  }).join('')}
          </tbody>
        </table>
      </div>

      <!-- Top Impressoras -->
      <div class="section">
        <h2 class="section-title">🖨️ Top Impressoras por Etiquetas</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 40px;">#</th>
              <th>Impressora</th>
              <th style="text-align: right;">Etiquetas</th>
              <th style="text-align: right;">%</th>
            </tr>
          </thead>
          <tbody>
            ${(topImpressoras || []).slice(0, 10).map((impressora, index) => {
    const percentual = ((impressora.etiquetasImp / (metricas.totalEtiquetas || 1)) * 100).toFixed(1);
    return `
                <tr>
                  <td style="text-align: center; font-weight: 600;">${index + 1}</td>
                  <td style="font-weight: 500;">${impressora.label || 'N/A'}</td>
                  <td style="text-align: right; color: #722ed1;">${(impressora.etiquetasImp || 0).toLocaleString()}</td>
                  <td style="text-align: right; font-weight: 500;">${percentual}%</td>
                </tr>
              `;
  }).join('')}
          </tbody>
        </table>
      </div>

      <!-- Top Filiais -->
      <div class="section">
        <h2 class="section-title">🏢 Top Filiais por Etiquetas</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 40px;">#</th>
              <th>Filial</th>
              <th style="text-align: right;">Impressas</th>
              <th style="text-align: right;">Pendentes</th>
              <th style="text-align: right;">Total</th>
              <th style="text-align: right;">%</th>
            </tr>
          </thead>
          <tbody>
            ${topFiliais.map((filial, index) => {
    const percentual = ((filial.total / (metricas.totalEtiquetas || 1)) * 100).toFixed(1);
    return `
                <tr>
                  <td style="text-align: center; font-weight: 600;">${index + 1}</td>
                  <td style="font-weight: 500;">${filial.filial}</td>
                  <td style="text-align: right; color: #52c41a;">${(filial.impressas || 0).toLocaleString()}</td>
                  <td style="text-align: right; color: #ff4d4f;">${(filial.pendentes || 0).toLocaleString()}</td>
                  <td style="text-align: right; font-weight: 600;">${(filial.total || 0).toLocaleString()}</td>
                  <td style="text-align: right; color: #1890ff;">${percentual}%</td>
                </tr>
              `;
  }).join('')}
          </tbody>
        </table>
      </div>

      <!-- Top Carros por Filial -->
      <div class="section">
        <h2 class="section-title">🚛 Top Carro por Filial</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 40px;">#</th>
              <th>Filial</th>
              <th>Carro</th>
              <th style="text-align: right;">Impressas</th>
              <th style="text-align: right;">Pendentes</th>
              <th style="text-align: right;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${topCarros.map((carro, index) => `
              <tr>
                <td style="text-align: center; font-weight: 600;">${index + 1}</td>
                <td style="font-weight: 500;">${carro.filial}</td>
                <td style="color: #1890ff;">${carro.carro}</td>
                <td style="text-align: right; color: #52c41a;">${(carro.impressas || 0).toLocaleString()}</td>
                <td style="text-align: right; color: #ff4d4f;">${(carro.pendentes || 0).toLocaleString()}</td>
                <td style="text-align: right; font-weight: 600;">${(carro.total || 0).toLocaleString()}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      ${includeCharts && Object.keys(chartImages).length > 0 ? `
      <!-- Gráficos -->
      <div class="section">
        <h2 class="section-title">📊 Gráficos de Análise e Distribuição</h2>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
          ${chartImages.topVeiculos ? `
          <div style="margin-bottom: 20px; page-break-inside: avoid;">
            <h3 style="color: #1890ff; margin-bottom: 10px; font-size: 14px;">Top 10 Veículos</h3>
            <div style="text-align: center;">
              <img src="${chartImages.topVeiculos}" alt="Top Veículos" style="max-width: 100%; height: auto; border: 1px solid #e8e8e8; border-radius: 4px;" />
            </div>
          </div>
          ` : ''}
          
          ${chartImages.topClientes ? `
          <div style="margin-bottom: 20px; page-break-inside: avoid;">
            <h3 style="color: #52c41a; margin-bottom: 10px; font-size: 14px;">Top 10 Clientes</h3>
            <div style="text-align: center;">
              <img src="${chartImages.topClientes}" alt="Top Clientes" style="max-width: 100%; height: auto; border: 1px solid #e8e8e8; border-radius: 4px;" />
            </div>
          </div>
          ` : ''}

          ${chartImages.filiais ? `
          <div style="margin-bottom: 20px; page-break-inside: avoid;">
            <h3 style="color: #ff8c00; margin-bottom: 10px; font-size: 14px;">Etiquetas por Filial</h3>
            <div style="text-align: center;">
              <img src="${chartImages.filiais}" alt="Etiquetas por Filial" style="max-width: 100%; height: auto; border: 1px solid #e8e8e8; border-radius: 4px;" />
            </div>
          </div>
          ` : ''}

          ${chartImages.carrosFilial ? `
          <div style="margin-bottom: 20px; page-break-inside: avoid;">
            <h3 style="color: #1890ff; margin-bottom: 10px; font-size: 14px;">Top 1 Carro por Filial</h3>
            <div style="text-align: center;">
              <img src="${chartImages.carrosFilial}" alt="Top Carro por Filial" style="max-width: 100%; height: auto; border: 1px solid #e8e8e8; border-radius: 4px;" />
            </div>
          </div>
          ` : ''}
        </div>

        ${chartImages.daily ? `
        <div style="margin-bottom: 30px; page-break-inside: avoid;">
          <h3 style="color: #1890ff; margin-bottom: 15px; font-size: 16px;">Volume Diário de Etiquetas</h3>
          <div style="text-align: center;">
            <img src="${chartImages.daily}" alt="Volume Diário" style="max-width: 100%; height: auto; border: 1px solid #e8e8e8; border-radius: 6px;" />
          </div>
        </div>
        ` : ''}
        
        ${chartImages.stacked ? `
        <div style="margin-bottom: 30px; page-break-inside: avoid;">
          <h3 style="color: #fa8c16; margin-bottom: 15px; font-size: 16px;">Distribuição Diária (Impressas vs Pendentes)</h3>
          <div style="text-align: center;">
            <img src="${chartImages.stacked}" alt="Distribuição Diária" style="max-width: 100%; height: auto; border: 1px solid #e8e8e8; border-radius: 6px;" />
          </div>
        </div>
        ` : ''}

        ${chartImages.heatmap ? `
        <div style="margin-bottom: 30px; page-break-inside: avoid;">
          <h3 style="color: #e6452c; margin-bottom: 15px; font-size: 16px;">Heatmap de Horários (Dia x Hora)</h3>
          <div style="text-align: center;">
            <img src="${chartImages.heatmap}" alt="Heatmap" style="max-width: 100%; height: auto; border: 1px solid #e8e8e8; border-radius: 6px;" />
          </div>
        </div>
        ` : ''}

        ${chartImages.impressorasPorDiaFilial ? `
        <div style="margin-bottom: 30px; page-break-inside: avoid;">
          <h3 style="color: #4f46e5; margin-bottom: 15px; font-size: 16px;">Impressoras Utilizadas por Dia e Filial</h3>
          <div style="text-align: center; overflow-x: auto;">
            <img src="${chartImages.impressorasPorDiaFilial}" alt="Impressoras Utilizadas por Dia e Filial" style="max-width: 100%; height: auto; border: 1px solid #e8e8e8; border-radius: 6px;" />
          </div>
        </div>
        ` : ''}
      </div>
      ` : ''}

      <!-- Volume Diário -->
      <div class="section">
        <h2 class="section-title">📅 Volume Diário (Últimos ${Math.min(dailyAgg.length, 15)} dias)</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th>Data</th>
              <th style="text-align: right;">Impressas</th>
              <th style="text-align: right;">Pendentes</th>
              <th style="text-align: right;">Total</th>
              <th style="text-align: right;">% do Perãodo</th>
            </tr>
          </thead>
          <tbody>
            ${dailyAgg.slice(-15).map(day => {
    const total = day.etiquetasImp + day.etiquetasSem;
    const percentual = ((total / (metricas.totalEtiquetas || 1)) * 100).toFixed(1);
    return `
                <tr>
                  <td style="font-weight: 500;">${dayjs(day.day).format('DD/MM/YYYY')}</td>
                  <td style="text-align: right; color: #52c41a;">${(day.etiquetasImp || 0).toLocaleString()}</td>
                  <td style="text-align: right; color: #ff4d4f;">${(day.etiquetasSem || 0).toLocaleString()}</td>
                  <td style="text-align: right; font-weight: 600;">${(total || 0).toLocaleString()}</td>
                  <td style="text-align: right; color: #1890ff;">${percentual}%</td>
                </tr>
              `;
  }).join('')}
          </tbody>
        </table>
      </div>
    </div>
  </div>
</body>
</html>
  `;
};